/**
 * <s-scratch-popup>: opens the kraskaart dialog after `data-delay` seconds.
 * - Shows at most once per visit (browser session): once it has opened, other pages in the same visit skip it.
 * - Closed or left without an email: it comes back on the next visit.
 * - Email submitted (event "scratch:subscribed"): never shown again in this browser.
 * `?kraskaart=1` in the URL forces it open for checking.
 */
const KEY = "steamrs:scratch-popup";
const SESSION_KEY = "steamrs:scratch-popup:seen";

class ScratchPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector("dialog");
    if (!this.dialog) return;
    this.ac = new AbortController();
    const opts = { signal: this.ac.signal };

    const forced = new URLSearchParams(location.search).has("kraskaart");
    if (!forced && !this.#shouldShow()) return;

    // Backdrop click closes, like the theme's other dialogs.
    this.dialog.addEventListener(
      "click",
      (e) => {
        if (e.target === this.dialog) this.dialog.close();
      },
      opts
    );
    this.dialog.addEventListener("close", () => this.#remember("closed"), opts);
    this.addEventListener("scratch:subscribed", () => this.#remember("subscribed"), opts);

    const delay = forced ? 0 : Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    this.timer = setTimeout(() => {
      if (this.dialog.open || document.querySelector("dialog[open]")) return;
      this.dialog.showModal();
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch (e) {}
    }, delay);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.ac?.abort();
  }

  #shouldShow() {
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return false;
      const saved = JSON.parse(localStorage.getItem(KEY) || "null");
      if (!saved) return true;
      if (saved.state === "subscribed") return false;
      // Closed without an email: show again on this new visit.
      return true;
    } catch (e) {
      return true;
    }
  }

  #remember(state) {
    try {
      const saved = JSON.parse(localStorage.getItem(KEY) || "null");
      if (saved?.state === "subscribed") return;
      localStorage.setItem(KEY, JSON.stringify({ state, at: Date.now() }));
    } catch (e) {}
  }
}

if (!customElements.get("s-scratch-popup")) customElements.define("s-scratch-popup", ScratchPopup);
